import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Select, InputNumber } from 'antd'
import Button from '../../components/Button'
import {
	MONTH_MAX,
	MONTH_MIN,
	MONTH_OTHER,
	MONTH_PRESETS,
	RATE_MAX,
	RATE_MIN,
} from './constants'

const monthOptions = [
	...MONTH_PRESETS.map((m) => ({ value: m, label: `${m} oy` })),
	{ value: MONTH_OTHER, label: 'Boshqa…' },
]

const isBlank = (v) => v === null || v === undefined || v === ''


const SettingModal = ({
	open,
	branches,
	settings,
	setting,
	defaultBranchId,
	onSubmit,
	onCancel,
}) => {
	const [branchId, setBranchId] = useState('')
	const [monthPreset, setMonthPreset] = useState(MONTH_PRESETS[0])
	const [monthCustom, setMonthCustom] = useState(null)
	const [rate, setRate] = useState(null)
	const [errors, setErrors] = useState({})
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		if (!open) return
		const m = setting?.monthCount
		setBranchId(setting?.branchId || defaultBranchId || '')
		if (m && MONTH_PRESETS.includes(m)) {
			setMonthPreset(m)
			setMonthCustom(null)
		} else if (m) {
			setMonthPreset(MONTH_OTHER)
			setMonthCustom(m)
		} else {
			setMonthPreset(MONTH_PRESETS[0])
			setMonthCustom(null)
		}
		setRate(setting ? setting.rate : null)
		setErrors({})
		setSaving(false)
	}, [open, setting, defaultBranchId])

	const monthCount = monthPreset === MONTH_OTHER ? monthCustom : monthPreset

	const branchOptions = useMemo(
		() => branches.map((b) => ({ value: b.id, label: b.name })),
		[branches],
	)

	const clearError = (key) => setErrors((p) => ({ ...p, [key]: '' }))

	const validate = () => {
		const e = {}
		if (!branchId) e.branchId = 'Filialni tanlang'

		if (isBlank(monthCount) || Number(monthCount) === 0)
			e.monthCount = 'Oylar sonini kiriting'
		else if (!Number.isInteger(Number(monthCount)))
			e.monthCount = "Oylar soni butun son bo'lishi kerak"
		else if (Number(monthCount) < MONTH_MIN)
			e.monthCount = 'Oylar sonini kiriting'
		else if (Number(monthCount) > MONTH_MAX)
			e.monthCount = `Oylar soni ${MONTH_MAX} dan oshmasligi kerak`

		if (isBlank(rate)) e.rate = 'Foizni kiriting'
		else if (Number(rate) < RATE_MIN || Number(rate) > RATE_MAX)
			e.rate = `Foiz ${RATE_MIN} va ${RATE_MAX} orasida bo'lishi kerak`

		if (!e.branchId && !e.monthCount) {
			const duplicate = settings.find(
				(s) =>
					s.branchId === branchId &&
					Number(s.monthCount) === Number(monthCount) &&
					s.id !== setting?.id,
			)
			if (duplicate)
				e.monthCount = `Bu filialda ${monthCount} oylik shart allaqachon mavjud`
		}

		setErrors(e)
		return Object.keys(e).length === 0
	}

	const submit = async () => {
		if (saving) return
		if (!validate()) return
		setSaving(true)
		try {
			const ok = await onSubmit({
				branchId,
				monthCount: Number(monthCount),
				rate: Number(rate),
			})
			if (!ok) setSaving(false)
		} catch (e) {
			setSaving(false)
		}
	}

	return (
		<Modal
			open={open}
			title={setting ? 'Sozlamani tahrirlash' : 'Nasiya sozlamasi qo‘shish'}
			onCancel={saving ? undefined : onCancel}
			footer={null}
			destroyOnClose
			maskClosable={!saving}
		>
			<div className="flex flex-col gap-4 mt-4">
				<div className="flex flex-col gap-1">
					<label className="text-sm font-medium">Filial</label>
					<Select
						value={branchId || undefined}
						options={branchOptions}
						placeholder="Filialni tanlang"
						showSearch
						optionFilterProp="label"
						status={errors.branchId ? 'error' : ''}
						onChange={(v) => {
							setBranchId(v)
							clearError('branchId')
						}}
						disabled={saving}
					/>
					{errors.branchId && (
						<p className="text-red-500 text-sm">{errors.branchId}</p>
					)}
				</div>

				<div className="flex flex-col gap-1">
					<label className="text-sm font-medium">Oylar soni</label>
					<div className="flex gap-2">
						<Select
							className="flex-1"
							value={monthPreset}
							options={monthOptions}
							status={errors.monthCount ? 'error' : ''}
							onChange={(v) => {
								setMonthPreset(v)
								clearError('monthCount')
							}}
							disabled={saving}
						/>
						{monthPreset === MONTH_OTHER && (
							<InputNumber
								className="flex-1"
								autoFocus
								value={monthCustom}
								min={MONTH_MIN}
								max={MONTH_MAX}
								precision={0}
								placeholder={`${MONTH_MIN}–${MONTH_MAX}`}
								status={errors.monthCount ? 'error' : ''}
								onChange={(v) => {
									setMonthCustom(v)
									clearError('monthCount')
								}}
								onPressEnter={submit}
								disabled={saving}
							/>
						)}
					</div>
					{errors.monthCount && (
						<p className="text-red-500 text-sm">{errors.monthCount}</p>
					)}
				</div>

				<div className="flex flex-col gap-1">
					<label className="text-sm font-medium">Foiz</label>
					<InputNumber
						className="w-full"
						value={rate}
						min={RATE_MIN}
						max={RATE_MAX}
						step={0.5}
						precision={3}
						decimalSeparator="."
						addonAfter="%"
						placeholder="Masalan: 20 yoki 32.5"
						status={errors.rate ? 'error' : ''}
						onChange={(v) => {
							setRate(v)
							clearError('rate')
						}}
						onPressEnter={submit}
						disabled={saving}
					/>
					{errors.rate && <p className="text-red-500 text-sm">{errors.rate}</p>}
				</div>
			</div>

			<div className="flex justify-end gap-2 mt-6">
				<Button
					className="!h-10 !bg-gray-400"
					onClick={onCancel}
					disabled={saving}
				>
					Bekor qilish
				</Button>
				<Button className="!h-10" onClick={submit} isLoading={saving}>
					Saqlash
				</Button>
			</div>
		</Modal>
	)
}

export default SettingModal
